import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PrimeReactProvider } from 'primereact/api';
import ConfirmationDialog from './modules/confirmation/components/ConfirmationDialog';
import Toast from './modules/notification/components/Toast';
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.min.css';


import 'primereact/resources/themes/md-light-indigo/theme.css';
// It should go after theme to override if any.
import './globals1.css';

import Home from './modules/home/views/HomeScreen';
import Account from './modules/account/views/AccountScreen';
import routes, { routeWithId } from './constants/routes';
import LoginScreen from './modules/authentication/login/views/LoginScreen';
import { Layout } from './components/Layout';
import TravelProgramScreen from './modules/travel-program/views/TravelProgramScreen';
import BillingTypeScreen from './modules/billing/views/BillingTypeScreen';
import PaymentMethodScreen from './modules/billing/views/PaymentMethodScreen';
import BillingScreen from './modules/billing/views/BillingScreen';
import BudgetManagementScreen from './modules/budget-management/views/BudgetManagementScreen';
import Billing from './modules/billing/compilations';
import ResetPasswordScreen from './modules/authentication/create-password/ResetPasswordScreen';
import ForgotPasswordScreen from './modules/authentication/forgot-password/views/ForgotPasswordScreen';
import InsightsScreen from './modules/insights/views/InsightsScreen';
import InsightsPdfViewer from './modules/insights/views/InsightsPdfViewer';
import SignOut from './modules/authentication/sign-out';
import AccountPendingScreen from './modules/onboarding/views/AccountPendingScreen';
import OTPScreen from './modules/authentication/login/views/OTPScreen';
import GroupInfoScreen from './modules/people/views/GroupInfoScreen';
import PeopleScreen from './modules/people/views/PeopleScreen';
import ViewPeopleScreen from './modules/people/views/ViewPeopleScreen';
import ProgramsScreen from './modules/programs/views/ProgramsScreen';
import EditProgramScreen from './modules/programs/views/EditProgramScreen';
import ViewProgramScreen from './modules/programs/views/ViewProgramScreen';
import SignUpStepOne from './modules/authentication/sign-up/views/SignUpStepOne';
import SignUpStepTwo from './modules/authentication/sign-up/views/SignUpStepTwo';
import SignUpStepThree from './modules/authentication/sign-up/views/SignUpStepThree';
import NiceModal from '@ebay/nice-modal-react';
import ConciergeStartScreen from './modules/concierge/concierge-start-screen/ConciergeStartScreen';
import ConciergeScreen from './modules/concierge/views/ConciergeScreen';
import SettingsScreen from './modules/account/views/SettingsScreen';
import EmailVerificationScreen from './modules/authentication/login/views/EmailVerificationScreen';
import ConciergeNewRide from './modules/concierge/new-ride/ConciergeNewRide';
import NewSendBooking from './modules/concierge/new-send-booking/NewSendBooking';
import { GuestRoute, OnboardingRoute, ProtectedRoute } from './components/routing';
import EmployeeSignUp from './modules/authentication/sign-up/views/EmployeeSignUp';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// let's disable that in dev to avoid http logs spamming.
			refetchOnWindowFocus: import.meta.env.DEV ? false : true,
		},
	},
});

function withLayout(Component: React.ComponentType) {
	return <Layout><Component /></Layout>;
}

function App() {
	return (
		<div className="font-poppins antialiased min-h-screen">
			{/* Here primeng has a mess with p-input-filled css class.
			1. The name is confusing. It has nothing to do with input which has filled value.
			2. The inputStyle: 'filled' option on PrimeReactProvider should add the class. But ... it works only for dialogs. But we need it everywhere. So, I set a class on body.
			Some context here: https://github.com/primefaces/primereact/issues/5053
			*/}
			<PrimeReactProvider value={{ ripple: true }}>
				<QueryClientProvider client={queryClient}>

					<ConfirmationDialog />
					<BrowserRouter>
						{/* Modals can have any content, so they need all stuff pages may need, including router */}
						<NiceModal.Provider>

							<Routes>
								<Route path={routes['/logout']} element={<SignOut />} />

								<Route element={<GuestRoute />}>
									<Route path={routes['/login']} element={<LoginScreen />} />
									<Route path={routes['/otp']} element={<OTPScreen />} />

									<Route path={routes['/sign-up']} element={<SignUpStepOne />} />
									<Route path={routes['/sign-up/create-credentials']} element={<SignUpStepTwo />} />
									<Route path={routes['/sign-up/company-details']} element={<SignUpStepThree />} />
									<Route path={routes['/verify-email']} element={<EmailVerificationScreen />} />
									<Route path={routes['/employee-signup']} element={<EmployeeSignUp />} />

									<Route path={routes['/forgot-password']} element={<ForgotPasswordScreen />} />
									<Route path={routes['/reset-password']} element={<ResetPasswordScreen />} />
								</Route>

								<Route element={<ProtectedRoute />}>
									<Route path={routes['/']} element={<Home />} />

									<Route element={<OnboardingRoute />}>
										<Route path={routes['/account']} element={<Account />} />
										<Route path={routes['/settings']} element={<SettingsScreen />} />

										<Route path={routes['/travel-program']} element={withLayout(TravelProgramScreen)} />

										<Route path={routes['/concierge-start']} element={withLayout(ConciergeStartScreen)} />
										<Route path={routes['/concierge/statuses/:id']} element={(<ConciergeScreen />)} />
										<Route path={routeWithId('/concierge/rides/:id', 0)} element={<ConciergeNewRide />} />
										<Route path={routeWithId('/concierge/delivery/:id', 0)} element={<NewSendBooking />} />

										<Route path={routes['/insights']} element={withLayout(InsightsScreen)} />
										<Route path={routes['/insights-pdf']} element={<InsightsPdfViewer />} />
										<Route path={routes['/people']} element={withLayout(PeopleScreen)} />
										<Route path={routes['/people/:id']} element={withLayout(ViewPeopleScreen)} />
										<Route path={routes['/people/group/:id']} element={withLayout(GroupInfoScreen)} />

										<Route path={routes['/insights']} element={withLayout(InsightsScreen)} />

										<Route path={routes['/programs']} element={withLayout(ProgramsScreen)} />
										<Route path={routes['/programs/edit']} element={withLayout(EditProgramScreen)} />
										<Route path={routes['/programs/id']} element={withLayout(ViewProgramScreen)} />

										<Route path={routes['/billing']} element={withLayout(BillingScreen)} />
										<Route path={routes['/billing/billing-type']} element={withLayout(BillingTypeScreen)} />
										<Route path={routes['/billing/payment-method']} element={withLayout(PaymentMethodScreen)} />

										<Route path={routes['/budget-management']} element={withLayout(BudgetManagementScreen)} />

										<Route path={routes['/compilations']} element={<Billing />} />
										{/* TODO: conditional onboarding screen: <Route path={routes['/']} element={<OnboardingScreen />} /> */}
										{/* <Route path={routes['/onboarding']} element={<OnboardingScreen />} /> */}
										{/* Not in the first version: <Route path={routes['/onboarding/program-suggestions']} element={<ProgramSuggestionScreen />} /> */}
										<Route path={routes['/onboarding/account-pending']} element={<AccountPendingScreen />} />
									</Route>
								</Route>
							</Routes>
						</NiceModal.Provider>
					</BrowserRouter>
					<Toast />
				</QueryClientProvider>
			</PrimeReactProvider>
		</div>
	);
}

export default App;
